<template>
  <div
    class="search-bar"
    :class="{
      'search-bar-loading': loading,
      'search-bar-disabled': disabled,
    }"
  >
    <input
      v-model="searchTerm"
      @input="onInput"
      @keydown.enter.prevent="onEnter"
      @keydown.down.prevent="onArrowDown"
      @keydown.up.prevent="onArrowUp"
      @keydown.esc="clearSuggestions"
      type="search"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="!disabled && visibleItems.length > 0"
      :aria-controls="visibleItems.length ? `${searchId}-list` : undefined"
      :aria-activedescendant="
        highlightedIndex >= 0 && highlightedIndex < visibleItems.length
          ? `${searchId}-option-${highlightedIndex}`
          : undefined
      "
      class="search-input"
      :placeholder="computedPlaceholder"
      autocomplete="off"
      :aria-label="computedPlaceholder"
      :disabled="disabled"
    />
    <span
      v-if="loading"
      class="search-loading-indicator"
      aria-hidden="true"
    ></span>
    <ul
      v-if="!disabled && visibleItems.length > 0"
      class="search-list"
      :id="`${searchId}-list`"
      role="listbox"
      :aria-label="computedPlaceholder"
    >
      <li
        v-for="(item, index) in visibleItems"
        :key="getItemKey(item, index)"
        :id="`${searchId}-option-${index}`"
        @click="selectItem(item)"
        @mouseenter="onMouseEnter(index)"
        :class="{ 'search-list-item-active': index === highlightedIndex }"
        class="search-list-item"
        role="option"
        :aria-selected="index === highlightedIndex"
      >
        <span class="search-list-label">{{ formatItem(item) }}</span>
        <button
          v-if="showAddButton && dataType === 'courses'"
          class="search-list-add"
          type="button"
          :aria-label="t('message.dashboard.addCourse')"
          @click.stop="addItem(item)"
        >
          +
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { inject, computed } from "vue";
let nextSearchId = 0;

export default {
  name: "SearchBar",
  props: {
    displayValue: { type: String, default: "" },
    items: {
      type: Array,
      default: () => [],
    },
    showAddButton: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dataType: {
      type: String,
      default: "courses", // or "institutions", "studyPrograms"
    },
  },
  setup(props) {
    const state = inject("state");

    const t = (key) => {
      const keys = key.split(".");
      let value = state.translations[state.locale];
      keys.forEach((k) => {
        value = value[k];
      });
      return value;
    };

    const computedPlaceholder = computed(() => {
      if (props.loading) {
        return t("message.search.loadingCourses");
      }
      switch (props.dataType) {
        case "institutions":
          return t("message.search.searchInstitution");
        case "studyPrograms":
          return t("message.search.searchStudyProgram");
        default:
          return t("message.search.placeholder");
      }
    });

    return { t, computedPlaceholder };
  },
  data() {
    return {
      searchId: `onlygrades-search-${++nextSearchId}`,
      searchTerm: "",
      filteredItems: [],
      highlightedIndex: -1,
      itemCache: {},
      visibleItems: [],
    };
  },
  created() {
    this.debouncedFilterItems = debounce(this.filterItems, 150);
  },
  beforeUnmount() {
    this.debouncedFilterItems?.cancel();
  },
  methods: {
    onInput() {
      if (this.disabled) return;
      this.debouncedFilterItems(this.searchTerm);
    },
    filterItems(term) {
      if (term !== this.searchTerm) return;
      if (!term.trim()) {
        this.clearSuggestions();
        return;
      }
      if (this.itemCache[term]) {
        this.filteredItems = this.itemCache[term];
      } else {
        const lowerCaseTerm = term.toLowerCase();
        this.filteredItems = this.items.filter((item) =>
          this.filterFunction(item, lowerCaseTerm)
        );
        this.itemCache[term] = this.filteredItems;
      }
      this.updateVisibleItems();
      this.highlightedIndex = -1;
    },
    filterFunction(item, term) {
      if (this.dataType === "courses") {
        return (
          item.course_name.toLowerCase().includes(term) ||
          item.course_code.toLowerCase().includes(term)
        );
      } else if (
        this.dataType === "institutions" ||
        this.dataType === "studyPrograms"
      ) {
        return item.name.toLowerCase().includes(term);
      }
      return false;
    },
    updateVisibleItems() {
      this.visibleItems = this.filteredItems.slice(0, 20);
    },
    formatItem(item) {
      if (this.dataType === "courses") {
        return `${item.course_code} - ${item.course_name}`;
      } else {
        return item.name;
      }
    },
    getItemKey(item, index) {
      if (this.dataType === "courses") {
        return `${item.course_code || ""}-${index}`;
      }
      return `${item.name || "item"}-${index}`;
    },
    addItem(item) {
      if (this.disabled) return;
      this.$emit("add", item);
      this.searchTerm = "";
      this.clearSuggestions();
    },
    clearSuggestions() {
      this.debouncedFilterItems?.cancel();
      this.filteredItems = [];
      this.visibleItems = [];
      this.highlightedIndex = -1;
    },
    selectItem(item) {
      if (this.disabled) return;
      this.searchTerm =
        this.showAddButton && this.dataType === "courses"
          ? ""
          : this.formatItem(item);
      this.$emit("search", item);
      this.clearSuggestions();
    },
    onArrowDown() {
      if (this.disabled) return;
      if (this.highlightedIndex < this.visibleItems.length - 1) {
        this.highlightedIndex++;
      } else {
        this.highlightedIndex = 0;
      }
    },
    onArrowUp() {
      if (this.disabled) return;
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      } else {
        this.highlightedIndex = this.visibleItems.length - 1;
      }
    },
    onEnter() {
      if (this.disabled) return;
      if (
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.visibleItems.length
      ) {
        this.selectItem(this.visibleItems[this.highlightedIndex]);
        return;
      }

      const raw = (this.searchTerm || "").trim();
      if (!raw) return;

      if (this.dataType === "courses") {
        const code = raw.split(" - ")[0].trim();
        this.$emit("search", { course_code: code });
        if (this.showAddButton) this.searchTerm = "";
        this.clearSuggestions();
      }
    },
    onMouseEnter(index) {
      if (this.disabled) return;
      this.highlightedIndex = index;
    },
  },
  watch: {
    displayValue: {
      immediate: true,
      handler(value) {
        this.searchTerm = value;
        this.clearSuggestions();
      },
    },
    items() {
      this.itemCache = {};
      this.clearSuggestions();
    },
    disabled(nextValue) {
      if (!nextValue) return;
      this.filteredItems = [];
      this.visibleItems = [];
      this.highlightedIndex = -1;
    },
  },
};
</script>

<style scoped>
.search-bar {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  background: var(--og-surface);
  padding: 0.7rem;
  border-radius: 16px;
  border: 1px solid var(--og-border);
  box-shadow: 0 3px 12px rgba(15, 23, 42, 0.05);
}

.search-bar-disabled {
  opacity: 0.82;
}

.search-input {
  padding: 0.78rem 0.95rem;
  border: 1px solid var(--og-border);
  border-radius: 12px;
  width: 100%;
  min-height: 44px;
  box-sizing: border-box;
  color: var(--og-text);
  background: var(--og-surface-soft);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-size: 1rem;
}

.search-input:disabled {
  cursor: not-allowed;
}

.search-input::placeholder {
  color: var(--og-muted);
}

.search-input:focus {
  outline: none;
  border-color: var(--og-primary);
  box-shadow: 0 0 0 2px rgba(47, 111, 235, 0.14);
}

.search-loading-indicator {
  width: 18px;
  height: 18px;
  margin-left: 0.55rem;
  border-radius: 999px;
  border: 2px solid rgba(100, 116, 139, 0.24);
  border-top-color: var(--og-primary);
  animation: search-spin 0.8s linear infinite;
  flex: 0 0 auto;
}

@keyframes search-spin {
  to {
    transform: rotate(360deg);
  }
}

.search-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  border: 1px solid var(--og-border);
  list-style-type: none;
  padding: 0;
  margin: 0;
  background-color: var(--og-surface);
  z-index: 1000;
  max-height: min(52vh, 340px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 5px 16px rgba(15, 23, 42, 0.08);
  border-radius: 12px;
}

.search-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.55rem;
  padding: 0.72rem 0.9rem;
  cursor: pointer;
  border-bottom: 1px solid var(--og-border);
  color: var(--og-text);
  background-color: var(--og-surface);
  text-align: left;
}

.search-list-label {
  flex: 1;
  min-width: 0;
}

.search-list-add {
  border: 1px solid var(--og-border);
  border-radius: 999px;
  min-width: 26px;
  height: 26px;
  background: var(--og-surface-soft);
  color: var(--og-primary-strong);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
}

.search-list-add:hover {
  border-color: var(--og-primary);
}

.search-list-item:last-child {
  border-bottom: none;
}

.search-list-item:hover,
.search-list-item-active {
  background-color: var(--og-primary-soft);
  color: var(--og-text);
}

@media (max-width: 600px) {
  .search-bar {
    width: 100%;
    padding: 0.5rem;
    border-radius: 14px;
  }
  .search-input {
    padding: 0.66rem 0.8rem;
    min-height: 44px;
    font-size: 0.95rem;
  }
  .search-list {
    top: calc(100% + 6px);
    max-height: min(54vh, 320px);
    border-radius: 10px;
  }
  .search-list-item {
    padding: 0.68rem 0.72rem;
    font-size: 0.9rem;
  }
}
</style>
